/**
 * Replay timeline (MVP) — multi-track horizontal scrubber over the last 24h.
 * Track 1: voz (transmisiones por usuario, alturas según duración).
 * Track 2: eventos (SOS rojo, location-update verde, mission-event naranja).
 * Mini-mapa Leaflet con la posición del usuario al instante seleccionado.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Play, Pause, SkipBack, Hash } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Header from './Header';
import useStore from '../store/useStore';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const SPEED_OPTIONS = [0.5, 1, 2, 4];

function fmtTime(ts) {
  return new Date(ts).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

export default function ReplayPage() {
  const channels = useStore(s => s.channels);
  const [from, setFrom] = useState(() => new Date(Date.now() - 24 * 3600 * 1000).toISOString().slice(0, 16));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 16));
  const [channelFilter, setChannelFilter] = useState('');
  const [data, setData] = useState({ transmissions: [], locations: [], events: [] });
  const [loading, setLoading] = useState(true);
  const [cursorMs, setCursorMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const playRef = useRef(null);

  const startMs = useMemo(() => new Date(from).getTime(), [from]);
  const endMs = useMemo(() => new Date(to).getTime(), [to]);
  const span = Math.max(endMs - startMs, 1);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('from', new Date(from).toISOString());
      params.set('to', new Date(to).toISOString());
      if (channelFilter) params.set('channelId', channelFilter);
      const r = await axios.get('/api/replay?' + params.toString());
      setData(r.data || { transmissions: [], locations: [], events: [] });
      setCursorMs(0);
    } catch (e) { console.warn('[Replay] load', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Animation loop for playback
  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const tick = (now) => {
      const dt = now - last;
      last = now;
      setCursorMs(prev => {
        const next = prev + dt * speed;
        if (next >= span) { setPlaying(false); return span; }
        return next;
      });
      playRef.current = requestAnimationFrame(tick);
    };
    playRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(playRef.current);
  }, [playing, speed, span]);

  const cursorTs = startMs + cursorMs;

  // Group transmissions by user for track 1 stacking
  const byUser = useMemo(() => {
    const m = new Map();
    for (const t of data.transmissions) {
      if (!m.has(t.userId)) m.set(t.userId, { username: t.username, avatarColor: t.avatarColor, items: [] });
      m.get(t.userId).items.push(t);
    }
    return Array.from(m.entries());
  }, [data.transmissions]);

  // Locations sorted by user — for the map polyline up to the cursor instant
  const userTrails = useMemo(() => {
    const m = new Map();
    for (const l of data.locations) {
      if (!m.has(l.userId)) m.set(l.userId, []);
      m.get(l.userId).push(l);
    }
    return m;
  }, [data.locations]);

  // Pick the most recent location <= cursor for the marker
  const cursorMarker = useMemo(() => {
    let pick = null;
    for (const [uid, pts] of userTrails.entries()) {
      for (let i = pts.length - 1; i >= 0; i--) {
        if (new Date(pts[i].recordedAt).getTime() <= cursorTs) { pick = { uid, pt: pts[i], trail: pts.slice(0, i + 1) }; break; }
      }
      if (pick) break;
    }
    return pick;
  }, [userTrails, cursorTs]);

  return (
    <div className="flex flex-col h-screen overflow-hidden" style={{ background: '#FBFBFD' }}>
      <Header />
      <div className="flex-1 overflow-hidden flex flex-col p-4 gap-3">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 p-3 rounded-md shadow-apple-sm" style={{ background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(0,0,0,0.05)' }}>
          <h1 className="text-lg font-bold text-n-800 tracking-apple-tight mr-2">Replay</h1>
          <div>
            <label className="block text-[10px] font-bold uppercase text-n-500 mb-1">Desde</label>
            <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)}
              className="text-xs px-2 py-1.5 rounded-sm border border-n-200" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-n-500 mb-1">Hasta</label>
            <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)}
              className="text-xs px-2 py-1.5 rounded-sm border border-n-200" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-n-500 mb-1">Canal</label>
            <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}
              className="text-xs px-2 py-1.5 rounded-sm border border-n-200">
              <option value="">— todos —</option>
              {(channels || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <button onClick={load} className="px-3 py-1.5 rounded-md text-xs font-semibold text-white shadow-apple-sm"
            style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}>
            Cargar
          </button>
          <span className="ml-auto text-[11px] text-n-500">
            {loading ? 'Cargando…' : `${data.transmissions.length} tx · ${data.locations.length} GPS · ${data.events.length} ev`}
          </span>
        </div>

        {/* Timeline + map */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 flex-1 overflow-hidden">
          {/* Timeline */}
          <div className="md:col-span-2 rounded-md p-3 flex flex-col gap-3 overflow-hidden" style={{ background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(0,0,0,0.05)' }}>
            <div className="flex items-center gap-2">
              <button onClick={() => { setCursorMs(0); setPlaying(false); }} className="p-2 rounded-md text-n-600 hover:bg-black/5"><SkipBack size={14} /></button>
              <button onClick={() => setPlaying(p => !p)}
                className="p-2 rounded-md text-white"
                style={{ background: 'linear-gradient(135deg,#007AFF,#AF52DE)' }}>
                {playing ? <Pause size={14} /> : <Play size={14} />}
              </button>
              <div className="flex items-center gap-1 ml-2">
                {SPEED_OPTIONS.map(s => (
                  <button key={s} onClick={() => setSpeed(s)}
                    className={`text-[10px] font-bold px-2 py-1 rounded-full ${speed === s ? 'text-white' : 'text-n-600'}`}
                    style={speed === s ? { background: '#007AFF' } : { background: '#F5F5F7' }}>
                    ×{s}
                  </button>
                ))}
              </div>
              <span className="ml-auto text-xs font-mono text-n-700">{new Date(cursorTs).toLocaleString('es-MX')}</span>
            </div>

            {/* Scrubber */}
            <input
              type="range" min={0} max={span} value={cursorMs} step={1000}
              onChange={(e) => setCursorMs(parseInt(e.target.value))}
              className="w-full"
            />

            {/* Track 1 — voice */}
            <div className="flex-1 overflow-y-auto scrollbar-thin">
              <div className="text-[10px] font-bold uppercase text-n-500 mb-1">Voz</div>
              {byUser.length === 0 && (
                <p className="text-[11px] text-n-400 italic">Sin transmisiones en el rango.</p>
              )}
              {byUser.map(([uid, info]) => (
                <div key={uid} className="mb-2">
                  <div className="text-[10px] text-n-500 mb-0.5">{info.username}</div>
                  <div className="relative h-7 rounded-sm" style={{ background: '#F5F5F7' }}>
                    {info.items.map(t => {
                      const start = new Date(t.startedAt).getTime();
                      const left = ((start - startMs) / span) * 100;
                      const width = Math.max(((t.duration || 0.5) * 1000 / span) * 100, 0.3);
                      return (
                        <div key={t.id}
                          title={`${info.username} · ${Math.round(t.duration)}s · ${fmtTime(t.startedAt)}`}
                          className="absolute top-1 bottom-1 rounded-sm"
                          style={{
                            left: `${left}%`, width: `${width}%`,
                            background: t.transcription ? '#007AFF' : '#5856D6',
                            opacity: 0.85,
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Track 2 — events */}
              <div className="text-[10px] font-bold uppercase text-n-500 mb-1 mt-3">Eventos</div>
              <div className="relative h-8 rounded-sm" style={{ background: '#F5F5F7' }}>
                {data.events.map(e => {
                  const t = new Date(e.occurredAt).getTime();
                  const left = ((t - startMs) / span) * 100;
                  const color = e.type === 'sos' ? '#FF2D55' : e.type === 'mission' ? '#FF9F0A' : '#34C759';
                  return (
                    <div key={e.id}
                      title={`${e.type} · ${e.username || '—'} · ${fmtTime(e.occurredAt)}`}
                      className="absolute top-1 bottom-1 w-1.5 rounded-sm"
                      style={{ left: `${left}%`, background: color, boxShadow: `0 0 8px ${color}99` }}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          {/* Mini-map */}
          <div className="rounded-md overflow-hidden" style={{ background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(0,0,0,0.05)' }}>
            <MapContainer
              center={cursorMarker?.pt ? [cursorMarker.pt.lat, cursorMarker.pt.lng] : [19.4326, -99.1332]}
              zoom={cursorMarker ? 14 : 5}
              style={{ height: '100%', minHeight: 320 }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {cursorMarker && (
                <>
                  <Polyline
                    positions={cursorMarker.trail.map(p => [p.lat, p.lng])}
                    pathOptions={{ color: '#007AFF', weight: 3, opacity: 0.6 }}
                  />
                  <Marker position={[cursorMarker.pt.lat, cursorMarker.pt.lng]} />
                </>
              )}
            </MapContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
